export type CompanyApprovalStatus = 'none' | 'pending' | 'approved' | 'rejected'

export interface CompanyApprovalStatusResponse {
  status: CompanyApprovalStatus
  companyId?: string | null
  companyName?: string | null
  rejectionReason?: string | null
}

export interface OnboardingStep {
  key: string
  label: string
  complete: boolean
  required: boolean
  /** Optional guidance from /api/me/onboarding-status (backward-compatible). */
  hint?: string | null
  /** Optional deep link from the API; falls back to client defaults. */
  href?: string | null
}

export interface OnboardingStatusResponse {
  complete: boolean
  currentStep: string | null
  progressPercent: number
  steps: OnboardingStep[]
}

export interface CompanyApplicationSummary {
  id: string
  name: string
  regCode?: string | null
  approvalStatus: string
  rejectionReason?: string | null
  adminNotes?: string | null
  submittedAt?: string | null
  approvedAt?: string | null
  ownerEmail?: string | null
  ownerName?: string | null
  ownerUserId?: string | null
  profileComplete?: boolean
  phoneVerified?: boolean
  addressProvided?: boolean
  documentCount?: number
  /** The company's package (service-core packages, Part A); absent on an older core. */
  package?: { id: string; name: string } | null
}

export interface CompanyApplicationDocument {
  id: string
  documentType: string
  fileName: string
  uploadedAt?: string | null
  openable?: boolean
}

export interface CompanyApplicationDetail extends CompanyApplicationSummary {
  ownerPhone?: string | null
  legalAddress?: string | null
  documents?: CompanyApplicationDocument[]
}

export interface VerificationStatusResponse {
  profileComplete: boolean
  phoneVerified: boolean
  addressProvided: boolean
}

/** Normalize approval status from /api/me or dedicated endpoint. */
export function normalizeApprovalStatus(
  status: string | null | undefined,
  companyId: string | null,
): CompanyApprovalStatus {
  if (!status || status === 'none') {
    return companyId ? 'pending' : 'none'
  }
  if (status === 'pending' || status === 'approved' || status === 'rejected') {
    return status
  }
  return companyId ? 'approved' : 'none'
}

/** Required go-live wizard order (team is optional and shown separately). */
export const WIZARD_STEP_ORDER = [
  'store',
  'catalog',
  'inventory',
  'offering',
  'payments',
] as const

export type WizardStepKey = (typeof WIZARD_STEP_ORDER)[number]

export const ONBOARDING_STEP_KEYS = [
  'company',
  'store',
  'catalog',
  'inventory',
  'offering',
  'payments',
  'team',
] as const

export type OnboardingStepKey = (typeof ONBOARDING_STEP_KEYS)[number]

export const DEFAULT_STEP_HREFS: Record<string, string> = {
  company: '/company',
  store: '/stores',
  catalog: '/categories',
  inventory: '/inventory',
  offering: '/offerings',
  payments: '/company',
  team: '/workers',
}

export const DEFAULT_STEP_HINTS: Record<string, string> = {
  company: 'Confirm your company profile is approved and ready.',
  store:
    'Create your first store with a name and address, and confirm weekly hours so clients know when you are open.',
  catalog: 'Pick a platform main product, then add at least one company subcategory under it.',
  inventory: 'Add a product (inventory item) under your subcategory.',
  offering:
    'Publish at least one offering with status Active. Draft offerings are not sellable.',
  payments:
    'Save a payment provider credential on Company (for example EveryPay username/secret) so you can take orders.',
  team:
    'Invite workers and managers, then assign a store manager for each store. Team setup is optional for go-live.',
}

export const AI_CAPABILITY_PROMPTS = {
  createStore:
    'Help me create my first store (outlet) on ComfortHub. Include name, address, and weekly opening hours. Propose the create action for Confirm.',
  subcategoryAndProduct:
    'Help me pick a platform main product, create a company subcategory under it, then add a product in inventory so my catalog is ready to sell.',
  publishActiveOffering:
    'Help me publish an Active offering linked to my product. Draft is not sellable — set status to Active, then propose the action for Confirm.',
  setOpeningStock:
    'Help me set opening stock for my product at my store. Propose adjusting stock for Confirm.',
  inviteWorker:
    'Help me invite a worker to my company. Draft the invite and propose the action for Confirm.',
  inviteManager:
    'Help me invite a manager to my company. Draft the invite and propose the action for Confirm.',
  assignStoreManager:
    'Help me assign a store manager to an outlet. List my stores and team, then propose the assignment for Confirm.',
  connectPayments:
    'Help me connect payments for go-live. Explain which Company credential fields I need (provider keys) and how to save them. Deep-link to /company if Propose is not the right fit.',
  finishNextStep:
    'Look at my go-live checklist and guide me through the next incomplete required step.',
  whatCanYouDo:
    'What can you help me with in the ComfortHub backoffice? Summarize capabilities and remind me that writes need Confirm.',
} as const

export type AiCapabilityPromptKey = keyof typeof AI_CAPABILITY_PROMPTS

export const CHECKLIST_GUIDE_CHIPS: {
  id: string
  label: string
  promptKey: AiCapabilityPromptKey
}[] = [
  { id: 'create-store', label: 'Create store', promptKey: 'createStore' },
  { id: 'subcategory-product', label: 'Subcategory + product', promptKey: 'subcategoryAndProduct' },
  { id: 'publish-offering', label: 'Publish Active offering', promptKey: 'publishActiveOffering' },
  { id: 'opening-stock', label: 'Set opening stock', promptKey: 'setOpeningStock' },
  { id: 'invite-worker', label: 'Invite worker', promptKey: 'inviteWorker' },
  { id: 'invite-manager', label: 'Invite manager', promptKey: 'inviteManager' },
  { id: 'assign-manager', label: 'Assign store manager', promptKey: 'assignStoreManager' },
  { id: 'connect-payments', label: 'Connect payments', promptKey: 'connectPayments' },
  { id: 'finish-next', label: 'Finish next step', promptKey: 'finishNextStep' },
]

export const ASSISTANT_STARTER_CHIPS: {
  id: string
  label: string
  promptKey: AiCapabilityPromptKey
}[] = [
  { id: 'invite-worker', label: 'Invite worker', promptKey: 'inviteWorker' },
  { id: 'assign-manager', label: 'Assign store manager', promptKey: 'assignStoreManager' },
  { id: 'publish-offering', label: 'Publish Active offering', promptKey: 'publishActiveOffering' },
  { id: 'finish-next', label: 'Finish next step', promptKey: 'finishNextStep' },
  { id: 'what-can-you-do', label: 'What can you do?', promptKey: 'whatCanYouDo' },
]

export function wizardStepIndex(key: string | null | undefined): number {
  if (!key) return 0
  const idx = WIZARD_STEP_ORDER.indexOf(key as WizardStepKey)
  return idx >= 0 ? idx : 0
}

export function hrefForStep(step: Pick<OnboardingStep, 'key' | 'href'>): string {
  if (step.href && step.href.trim()) return step.href.trim()
  return DEFAULT_STEP_HREFS[step.key] ?? '/onboarding'
}

export function hintForStep(step: Pick<OnboardingStep, 'key' | 'hint'>): string {
  if (step.hint && step.hint.trim()) return step.hint.trim()
  return DEFAULT_STEP_HINTS[step.key] ?? 'Continue this step to progress go-live.'
}

export function guidePromptForStep(stepKey: string): string {
  switch (stepKey) {
    case 'store':
      return AI_CAPABILITY_PROMPTS.createStore
    case 'catalog':
      return 'Help me finish the catalog step: pick a platform main product and create a company subcategory under it for go-live.'
    case 'inventory':
      return 'Help me add inventory (a product) under my subcategory so stock and offerings can be linked for go-live.'
    case 'offering':
      return AI_CAPABILITY_PROMPTS.publishActiveOffering
    case 'payments':
      return AI_CAPABILITY_PROMPTS.connectPayments
    case 'team':
      return 'Help me invite workers or managers and assign a store manager to an outlet. Team is optional for go-live but recommended.'
    case 'company':
      return 'Explain what is left on my company profile for go-live and how to finish it.'
    default:
      return AI_CAPABILITY_PROMPTS.finishNextStep
  }
}

/** KYC requirements before a platform admin can approve a pending application. */
export function kycReady(row: Pick<CompanyApplicationSummary, 'profileComplete' | 'phoneVerified' | 'addressProvided'>): boolean {
  return Boolean(row.profileComplete && row.phoneVerified && row.addressProvided)
}

export function kycBlockReason(
  row: Pick<CompanyApplicationSummary, 'profileComplete' | 'phoneVerified' | 'addressProvided'>,
): string | undefined {
  if (!row.profileComplete) return 'Profile incomplete (name + phone required)'
  if (!row.phoneVerified) return 'Phone not verified'
  if (!row.addressProvided) return 'Legal address missing'
  return undefined
}
